import React, { useEffect, useRef, useState } from "react";
import { Box, FileButton, Group, Stack, Text } from "@mantine/core";
import { Button } from "@xplatform/design-system";
import { IconFileSpreadsheet, IconUpload, IconX } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { formatMb } from "@utils/download";

interface Props {
  value: File | null;
  onChange: (file: File | null) => void;
  error: string | null;
  /** Review uploads: the only filename accepted. Omitted for correction lists (PBI #747204). */
  expectedFilename?: string;
  /** File types the chooser offers; default ".xlsx". */
  accept?: string;
  /** Texts for other uses than the review upload; default = review-upload texts. */
  labels?: { title: string; choose: string };
}

/**
 * The review-file picker (PBI #781340): drop a file onto the zone, or use the
 * "Choose .xlsx file" button. Native drag-and-drop events + Mantine FileButton, so no
 * extra package. A dropped file goes through the same onChange (and therefore the same
 * checks) as a chosen one; dropping several files is rejected rather than silently
 * taking the first.
 */
export const ReviewFileDrop: React.FC<Props> = ({ value, onChange, error, expectedFilename, accept = ".xlsx", labels }) => {
  const { t } = useTranslation();
  const [dragging, setDragging] = useState(false);
  const [dropError, setDropError] = useState<string | null>(null);
  const resetRef = useRef<() => void>(null);

  // Clear the hidden input when the selection is cleared, so choosing the same file again
  // still fires a change.
  useEffect(() => {
    if (!value) resetRef.current?.();
  }, [value]);

  const choose = (f: File | null) => {
    setDropError(null);
    onChange(f);
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length === 0) return;
    if (files.length > 1) {
      setDropError(t("reviews.upload.dropOneFile", { count: files.length }));
      return;
    }
    choose(files[0]);
  };

  const shownError = dropError ?? error;

  return (
    <Box
      data-testid="review-file-drop"
      data-dragging={dragging || undefined}
      onDragOver={(e) => {
        e.preventDefault(); // required, otherwise the browser opens the file instead
        e.dataTransfer.dropEffect = "copy";
        if (!dragging) setDragging(true);
      }}
      onDragLeave={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDragging(false);
      }}
      onDrop={onDrop}
      py="lg"
      px="md"
      style={{
        border: `2px dashed var(--mantine-color-${shownError ? "red-4" : dragging ? "blue-5" : "gray-4"})`,
        borderRadius: "var(--mantine-radius-md)",
        background: dragging ? "var(--mantine-color-blue-0)" : undefined,
        transition: "background 120ms, border-color 120ms",
      }}
    >
      <Stack gap={6} align="center">
        <Group gap="xs">
          <IconUpload size={18} />
          <Text fw={600}>{dragging ? t("reviews.upload.dropNow") : (labels?.title ?? t("reviews.upload.dropTitle"))}</Text>
        </Group>

        <Group gap="xs">
          <Text size="sm">{t("reviews.upload.or")}</Text>
          <FileButton onChange={choose} accept={accept} resetRef={resetRef}>
            {(props) => (
              <Button {...props} size="xs" variant="outline">
                {labels?.choose ?? t("reviews.upload.chooseFile")}
              </Button>
            )}
          </FileButton>
        </Group>

        {value && (
          <Group gap={6} data-testid="selected-file">
            <IconFileSpreadsheet size={16} />
            <Text size="sm">
              {value.name} — {formatMb(value.size)}
            </Text>
            <Button
              size="compact-xs"
              variant="subtle"
              color="gray"
              aria-label={t("reviews.upload.clearFile")}
              onClick={() => choose(null)}
            >
              <IconX size={14} />
            </Button>
          </Group>
        )}

        <Text size="xs" c="dimmed" ta="center">
          {expectedFilename && <>{t("reviews.upload.expectedFilename", { filename: expectedFilename })} · </>}
          {t("reviews.upload.maxSize")}
        </Text>
        {shownError && (
          <Text size="sm" c="red" ta="center" role="alert">
            {shownError}
          </Text>
        )}
      </Stack>
    </Box>
  );
};
